/**
 * TIME ZONES — C2-16 (UI-8) and the `timezone_override` setting from C2-13.
 *
 * =============================================================================
 * WHAT UI-8 ACTUALLY FOUND, WHICH IS NOT WHAT IT WAS LOOKING FOR
 * =============================================================================
 *
 * UI-8 asked whether a datetime survives the round trip. It does — the instant
 * has always been right, because the cell parsed the local string with
 * `new Date()` and stored `toISOString()`, and both of those speak the browser's
 * zone consistently.
 *
 * THE DEFECT IS THAT THE GRID USED TWO ZONES AT ONCE. Created and Updated were
 * rendered in `America/New_York` — a module constant, spec v2 V2-7 — while the
 * datetime EDITOR spoke the browser's zone, because `<input type="datetime-local">`
 * has no zone and `d.getHours()` is browser-local. On the mini those are the same
 * zone and nothing looks wrong. Open the console from anywhere else and a value
 * typed as 09:00 reads back as 09:00 in the box and as something else in the
 * column beside it, with no error and nothing to notice. That is the shape of
 * thing that is "silently wrong for six months".
 *
 * So the zone is now ONE choice, per person, and every path goes through this
 * file: the columns, the editor and the row history.
 *
 * =============================================================================
 * WHY THE PARSE PROPOSES TWO CANDIDATES AND THEN CHECKS THEM
 * =============================================================================
 *
 * Converting a wall-clock time in some zone to an instant is not a subtraction,
 * because the offset you need depends on the instant you are trying to find.
 * `2026-11-01 01:30` in America/New_York happens TWICE; `2026-03-08 02:30` never
 * happens at all.
 *
 * The obvious loop — guess, read the offset there, correct, read again — walks
 * straight into that and answers a GAP with a time an hour EARLIER than the one
 * asked for, which silently moves an appointment backwards. It was written that
 * way first and the DST test below is what caught it.
 *
 * So instead: take the offset well BEFORE the guess and well AFTER it, which
 * are the only two offsets in play around a transition, and form a candidate
 * instant from each. Then VERIFY each candidate by reading its wall clock back
 * and comparing it to what was asked for. That check is the whole point — it
 * turns "which offset applies" from a deduction into an observation.
 *
 *   both candidates read back correctly  -> the hour happens twice (an overlap).
 *                                           Take the EARLIER instant, which is
 *                                           the first of the two occurrences.
 *   exactly one reads back correctly     -> ordinary case, and the transition
 *                                           cases where only one side is real.
 *   neither reads back correctly         -> the hour does not exist (a gap).
 *                                           Take the LATER instant, so the time
 *                                           moves FORWARD out of the gap.
 *
 * That is what `Temporal`'s `compatible` disambiguation does, and what Luxon and
 * every calendar application do, so a person moving between this console and any
 * other tool gets the same answer.
 *
 * =============================================================================
 * NO LIBRARY
 * =============================================================================
 *
 * `Intl.DateTimeFormat` already carries the IANA database in every browser this
 * console supports, and the validation on the other end is Postgres's own
 * `pg_timezone_names` — the same database again. Adding a date library would put
 * a THIRD copy of the zone rules in the stack and it would be the only one of
 * the three that ages on a deploy cadence.
 */

/**
 * THE HOUSE ZONE — Matt's ruling, M1-7.
 *
 * The grid does not USE this to render anything: a browser follows the browser
 * when the setting is empty, and that has not changed. It is here so the
 * timezone control can TELL a person what an empty setting means for the other
 * reader of the same row — their Claude, which has no browser and resolves an
 * empty setting to this zone.
 *
 * It is a second copy of `HOUSE_ZONE` in server/src/lib/tz.ts, and the two are
 * asserted equal in server/test/tz.test.ts rather than trusted, because a
 * control that explains a rule while naming a different zone from the rule is
 * worse than one that explains nothing.
 */
export const HOUSE_ZONE = 'America/New_York';

export type DateFormat = 'iso' | 'us' | 'long';
export type TimeFormat = '24h' | '12h';

export interface DisplayPrefs {
  /** '' means "follow this browser", which is the catalogue's default. */
  tz: string;
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
}

export const BROWSER_TZ: DisplayPrefs = { tz: '', dateFormat: 'iso', timeFormat: '24h' };

/** The zone actually in force: the person's choice, or this browser's. */
export function zoneOf(prefs: DisplayPrefs): string {
  if (prefs.tz) return prefs.tz;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

interface Wall { year: number; month: number; day: number; hour: number; minute: number; second: number }

/** What the wall clock reads in `tz` at instant `d`. */
export function wallClockIn(d: Date, tz: string): Wall {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t: string): number => Number(parts.find((p) => p.type === t)?.value ?? '0');
  // `hour12: false` renders midnight as 24 in some engines. 24:00 of a day is
  // 00:00 of that same day here, because the date parts are already that day's.
  const hour = get('hour') % 24;
  return { year: get('year'), month: get('month'), day: get('day'), hour, minute: get('minute'), second: get('second') };
}

/** How far `tz` is from UTC at instant `d`, in milliseconds. */
function offsetMsAt(d: Date, tz: string): number {
  const w = wallClockIn(d, tz);
  const asIfUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  // Milliseconds are not in the parts, so compare on whole seconds.
  return asIfUtc - Math.floor(d.getTime() / 1000) * 1000;
}

const DAY_MS = 86_400_000;

/**
 * `"2026-03-08T02:30"` in `tz` -> the ISO instant, or '' if it is not a
 * datetime-local string. See the header for the two candidates, the check, and
 * what happens on a gap and on an overlap.
 */
export function zonedToIso(local: string, tz: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local.trim());
  if (!m) return '';
  const asked = {
    year: Number(m[1]), month: Number(m[2]), day: Number(m[3]),
    hour: Number(m[4]), minute: Number(m[5]), second: Number(m[6] ?? '0'),
  };
  const guess = Date.UTC(asked.year, asked.month - 1, asked.day, asked.hour, asked.minute, asked.second);

  // The two offsets in play around any transition. A day either side is wider
  // than every real transition and narrower than every zone's rule period.
  const candidates = [
    guess - offsetMsAt(new Date(guess - DAY_MS), tz),
    guess - offsetMsAt(new Date(guess + DAY_MS), tz),
  ];

  const reads = (ts: number): boolean => {
    const w = wallClockIn(new Date(ts), tz);
    return w.year === asked.year && w.month === asked.month && w.day === asked.day
      && w.hour === asked.hour && w.minute === asked.minute;
  };

  const valid = [...new Set(candidates)].filter(reads);
  // Overlap or ordinary: the earliest instant that genuinely reads back as the
  // time asked for. Gap: nothing reads back, so take the later candidate, which
  // moves the time forward out of the gap rather than backwards into the day
  // before it.
  const ts = valid.length ? Math.min(...valid) : Math.max(...candidates);
  return new Date(ts).toISOString();
}

/** The other direction: an ISO instant -> what `<input type="datetime-local">` wants. */
export function isoToZoned(iso: string, tz: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const w = wallClockIn(d, tz);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${w.year}-${p(w.month)}-${p(w.day)}T${p(w.hour)}:${p(w.minute)}`;
}

/** An ISO instant -> `yyyy-MM-dd` as the date reads in `tz`. */
export function isoToZonedDate(iso: string, tz: string): string {
  return isoToZoned(iso, tz).slice(0, 10);
}

/**
 * A stored instant, rendered for a person.
 *
 * Every format here is explicit rather than a locale default, because a locale
 * default is a fourth thing that can differ between two browsers looking at the
 * same row.
 */
export function formatInstant(iso: unknown, prefs: DisplayPrefs): string {
  if (typeof iso !== 'string' || !iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const tz = zoneOf(prefs);
  const w = wallClockIn(d, tz);
  return `${formatWallDate(w, prefs.dateFormat)} ${formatWallTime(w, prefs.timeFormat)}`;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatWallDate(w: Wall, f: DateFormat): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  if (f === 'us') return `${w.month}/${w.day}/${w.year}`;
  if (f === 'long') return `${w.day} ${MONTHS[w.month - 1]} ${w.year}`;
  return `${w.year}-${p(w.month)}-${p(w.day)}`;
}

function formatWallTime(w: Wall, f: TimeFormat): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  if (f === '12h') {
    const h = w.hour % 12 === 0 ? 12 : w.hour % 12;
    return `${h}:${p(w.minute)} ${w.hour < 12 ? 'AM' : 'PM'}`;
  }
  return `${p(w.hour)}:${p(w.minute)}`;
}

/** A date-only value (no time), rendered in the person's date format. */
export function formatDateOnly(value: unknown, prefs: DisplayPrefs): string {
  if (typeof value !== 'string' || !value) return '';
  // SLICED, NOT PARSED. A date column has no instant in it — `new Date()` would
  // read '2026-08-27' as UTC midnight and render the day before for anybody west
  // of Greenwich, which is the defect the date CELL already carries a comment
  // about. The zone is deliberately not consulted here.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  return formatWallDate(
    { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]), hour: 0, minute: 0, second: 0 },
    prefs.dateFormat,
  );
}
